import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthStore } from '../store/authStore';
import { FiMail, FiLock, FiArrowRight, FiEye, FiEyeOff, FiArrowLeft, FiShield } from 'react-icons/fi';
import { t as tr, Lang } from '../utils/translations';
import api from '../api/axios';
import './AuthPages.css';

const stripArabic = (v: string) => v.replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g, '');

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [lang, setLang] = useState<Lang>(() => (localStorage.getItem('cartz_lang') as Lang) || 'en');
  const t = (key: string) => tr(key, lang);
  const isRTL = lang === 'ar';

  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Reset password state
  const [resetMode, setResetMode] = useState(false);
  const [resetStep, setResetStep] = useState<'email' | 'code'>('email');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success(isRTL ? 'مرحباً بعودتك!' : 'Welcome back!');
      navigate('/dashboard');
    } catch (err: any) {
      const msg = err.response?.data?.message || '';
      const arMap: Record<string, string> = {
        'Invalid credentials.': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
        'Invalid or expired reset code.': 'رمز إعادة التعيين غير صالح أو منتهي',
        'Invalid reset code.': 'رمز إعادة التعيين غير صالح',
      };
      toast.error(isRTL ? (arMap[msg] || 'فشل تسجيل الدخول') : (msg || 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    setResetLoading(true);
    try {
      await api.post('/forgot-password', { email: resetEmail });
      toast.success(isRTL ? 'تم إرسال رمز إعادة التعيين إلى بريدك الإلكتروني' : 'Reset code sent to your email');
      setResetStep('code');
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'حدث خطأ' : 'Something went wrong'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetPassword !== resetConfirm) {
      toast.error(isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return;
    }
    setResetLoading(true);
    try {
      await api.post('/reset-password', {
        email: resetEmail,
        code: resetCode,
        password: resetPassword,
        password_confirmation: resetConfirm,
      });
      toast.success(isRTL ? 'تم إعادة تعيين كلمة المرور بنجاح' : 'Password reset successfully!');
      exitReset();
    } catch (err: any) {
      toast.error(err.response?.data?.message || (isRTL ? 'فشل إعادة التعيين' : 'Reset failed'));
    } finally {
      setResetLoading(false);
    }
  };

  const exitReset = () => {
    setResetMode(false);
    setResetStep('email');
    setResetEmail('');
    setResetCode('');
    setResetPassword('');
    setResetConfirm('');
    setShowResetPassword(false);
  };

  const toggleLang = () => {
    const newLang = lang === 'en' ? 'ar' : 'en';
    setLang(newLang);
    localStorage.setItem('cartz_lang', newLang);
  };

  return (
    <div className="auth-page" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="auth-left">
        <div className="auth-left-top">
          <Link to="/" className="landing-logo">
            <img className="cartz-mark" src="/favicon.svg" alt="" />
            <span className="logo-text">Cartz</span>
          </Link>
          <button className="sf-lang-toggle" onClick={toggleLang} style={{ marginInlineStart: 'auto' }}>
            {lang === 'en' ? 'عربي' : 'English'}
          </button>
        </div>
        <div className="auth-left-content">
          <h2>{t('auth.left.title.login')}</h2>
          <p>{t('auth.left.desc.login')}</p>
        </div>
      </div>
      <div className="auth-right">
        <div className="auth-form-wrapper">
          {!resetMode ? (
            <>
              <h1>{t('auth.login.title')}</h1>
              <p className="auth-subtitle">{t('auth.login.subtitle')}</p>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label className="form-label">{t('form.email')}</label>
                  <div className="input-icon">
                    <FiMail />
                    <input
                      type="email"
                      className="form-input"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: stripArabic(e.target.value) })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label className="form-label">{t('form.password')}</label>
                    <button
                      type="button"
                      className="forgot-password-link"
                      onClick={() => { setResetMode(true); setResetEmail(form.email); }}
                    >
                      {t('auth.login.forgot')}
                    </button>
                  </div>
                  <div className="input-icon">
                    <FiLock />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-input ltr-input"
                      placeholder={isRTL ? 'أدخل كلمة المرور' : 'Enter your password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: stripArabic(e.target.value) })}
                      required
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading}>
                  {loading ? t('auth.login.loading') : t('auth.login.btn')} <FiArrowRight style={isRTL ? { transform: 'rotate(180deg)' } : {}} />
                </button>
              </form>

              <p className="auth-switch">
                {t('auth.login.switch')} <Link to="/register">{t('auth.login.switch.link')}</Link>
              </p>
            </>
          ) : (
            <>
              <button type="button" className="reset-back-btn" onClick={exitReset}>
                <FiArrowLeft style={isRTL ? { transform: 'rotate(180deg)' } : {}} /> {t('reset.backToLogin')}
              </button>

              <div className="reset-icon-wrapper">
                <FiShield />
              </div>
              <h1>{t('reset.title')}</h1>
              <p className="auth-subtitle">
                {resetStep === 'email' ? t('reset.subtitle') : t('reset.codeSubtitle')}
              </p>

              {resetStep === 'email' ? (
                <form onSubmit={handleSendCode} className="auth-form">
                  <div className="form-group">
                    <label className="form-label">{t('form.email')}</label>
                    <div className="input-icon">
                      <FiMail />
                      <input
                        type="email"
                        className="form-input"
                        placeholder="you@example.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(stripArabic(e.target.value))}
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={resetLoading}>
                    {resetLoading ? t('reset.sending') : t('reset.sendCode')} <FiArrowRight style={isRTL ? { transform: 'rotate(180deg)' } : {}} />
                  </button>
                </form>
              ) : (
                <form onSubmit={handleResetPassword} className="auth-form">
                  <div className="form-group">
                    <label className="form-label">{t('reset.codeLabel')}</label>
                    <div className="input-icon">
                      <FiShield />
                      <input
                        type="text"
                        className="form-input reset-code-input"
                        placeholder={t('reset.codePlaceholder')}
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        required
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('reset.newPassword')}</label>
                    <div className="input-icon">
                      <FiLock />
                      <input
                        type={showResetPassword ? 'text' : 'password'}
                        className="form-input ltr-input"
                        placeholder={isRTL ? 'كلمة المرور الجديدة' : 'New password'}
                        value={resetPassword}
                        onChange={(e) => setResetPassword(stripArabic(e.target.value))}
                        required
                        minLength={8}
                      />
                      <button type="button" className="password-toggle" onClick={() => setShowResetPassword(!showResetPassword)}>
                        {showResetPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{t('reset.confirmPassword')}</label>
                    <div className="input-icon">
                      <FiLock />
                      <input
                        type={showResetPassword ? 'text' : 'password'}
                        className="form-input ltr-input"
                        placeholder={isRTL ? 'تأكيد كلمة المرور' : 'Confirm new password'}
                        value={resetConfirm}
                        onChange={(e) => setResetConfirm(stripArabic(e.target.value))}
                        required
                        minLength={8}
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={resetLoading || resetCode.length !== 6}>
                    {resetLoading ? t('reset.resetting') : t('reset.submit')} <FiArrowRight style={isRTL ? { transform: 'rotate(180deg)' } : {}} />
                  </button>

                  <p className="auth-switch" style={{ marginTop: 16 }}>
                    <button type="button" className="forgot-password-link" onClick={handleSendCode as any}>
                      {t('reset.resendCode')}
                    </button>
                  </p>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
